import React from 'react'

import { PrototypeErrorPage } from '../PrototypeErrorPage'
import { ReactComponent as ExclamationMarkTriangleIcon } from '@sketch/icons/exclamation-mark-triangle-64'

import { ReactComponent as GlobeIcon } from '@sketch/icons/globe-64'
import { ReactComponent as ImagePlaceholderIcon } from '@sketch/icons/image-placeholder-64'

import { Link, Button } from '@sketch/components'
import { PrototypeViewErrorCode } from '../../types'

type PrototypeErrorProps = {
  code: PrototypeViewErrorCode
}

/**
 * Component handling the display of a meaningful message based on the provided error.
 */
export function PrototypeLoadingErrorHandler({ code }: PrototypeErrorProps) {
  if (code === 'artboard_not_found') {
    return (
      <PrototypeErrorPage
        icon={<ImagePlaceholderIcon />}
        errorTitle="Cannot find Artboard"
        errorDescription="The Artboard might have been moved or deleted, or the URL could be incorrect."
      />
    )
  }

  if (code === 'offline') {
    return (
      <PrototypeErrorPage
        icon={<GlobeIcon />}
        errorTitle="You're offline."
        errorDescription="Check your connection and reload the page."
        errorExtra={
          <Button
            variant="primary-untinted"
            size="32"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        }
      />
    )
  }

  if (code === 'query_error') {
    return <GenericError title="Failed to load the Prototype data" />
  }

  if (code === 'invalid_manifest') {
    return <GenericError title="Prototype file is invalid" />
  }

  if (code === 'image_preload_error') {
    return <GenericError title="Failed to load the Prototype images" />
  }

  return <GenericError title="Something went wrong" />
}

function GenericError({ title }: { title: string }) {
  return (
    <PrototypeErrorPage
      icon={<ExclamationMarkTriangleIcon />}
      errorTitle={title}
      errorDescription={
        <>
          {"We're looking into the issue. Try again later or visit our "}
          <Link
            variant="quaternary"
            external={true}
            isUnderlined={true}
            title="Sketch status page"
            href="https://status.sketch.com/"
          >
            Status Page
          </Link>
          .
        </>
      }
    />
  )
}
